/**
 * QMS Stepper Component
 * Visual step indicator for multi-step wizards
 */

import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface WizardStep {
  id: string;
  title: string;
  description?: string;
}

export type StepStatus = 'pending' | 'active' | 'completed' | 'locked';

interface QmsStepperProps {
  steps: WizardStep[];
  currentStep: number;
  onStepClick?: (stepIndex: number) => void;
  completedSteps?: number[];
  lockedSteps?: number[];
}

export function QmsStepper({
  steps,
  currentStep,
  onStepClick,
  completedSteps = [],
  lockedSteps = [],
}: QmsStepperProps) {
  const getStepStatus = (index: number): StepStatus => {
    if (lockedSteps.includes(index)) return 'locked';
    if (completedSteps.includes(index)) return 'completed';
    if (index === currentStep) return 'active';
    return 'pending';
  };

  const handleStepClick = (index: number) => {
    const status = getStepStatus(index);
    if (status !== 'locked' && onStepClick) {
      onStepClick(index);
    }
  };

  return (
    <nav aria-label="Progress" className="w-full">
      <ol className="flex items-center">
        {steps.map((step, index) => {
          const status = getStepStatus(index);
          const isLast = index === steps.length - 1;

          return (
            <li
              key={step.id}
              className={cn('relative', !isLast && 'flex-1')}
            >
              <div className="flex items-center">
                {/* Step Circle */}
                <button
                  type="button"
                  onClick={() => handleStepClick(index)}
                  disabled={status === 'locked'}
                  className={cn(
                    'relative flex h-10 w-10 items-center justify-center rounded-full border-2 transition-colors',
                    status === 'completed' && 'border-green-600 bg-green-600 text-white',
                    status === 'active' && 'border-atek-blue bg-atek-blue text-white',
                    status === 'pending' && 'border-gray-300 bg-white text-gray-500 hover:border-gray-400',
                    status === 'locked' && 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed',
                    (status === 'pending' || status === 'completed') && onStepClick && 'cursor-pointer'
                  )}
                  aria-current={status === 'active' ? 'step' : undefined}
                >
                  {status === 'completed' ? (
                    <Check className="h-5 w-5" />
                  ) : (
                    <span className="text-sm font-semibold">{index + 1}</span>
                  )}
                </button>

                {/* Connector Line */}
                {!isLast && (
                  <div
                    className={cn(
                      'h-0.5 flex-1 mx-2',
                      completedSteps.includes(index) ? 'bg-green-600' : 'bg-gray-200'
                    )}
                  />
                )}
              </div>

              {/* Step Label */}
              <div className="mt-2">
                <span
                  className={cn(
                    'text-sm font-medium',
                    status === 'active' && 'text-atek-blue',
                    status === 'completed' && 'text-green-600',
                    status === 'pending' && 'text-gray-500',
                    status === 'locked' && 'text-gray-400'
                  )}
                >
                  {step.title}
                </span>
                {step.description && (
                  <p className="text-xs text-gray-400 mt-0.5">{step.description}</p>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
